import { SupportedRegions } from "@nova/constants"
import { addBlobToCache, createExternalStore, getCachedBlobUrl } from "@nova/toolkit-pure-hooks"
import { getColorFromId } from "@nova/util-strings"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"

// Just for resolution. Deck.gl renders based on getSize.
export const USER_LOCATION_ICON_SIZE = 256

const HEADING_INDICATOR_SIZE = 50

const RADIUS = USER_LOCATION_ICON_SIZE / 2
const AVATAR_RADIUS_RATIO = (RADIUS - HEADING_INDICATOR_SIZE) / RADIUS
const AVATAR_RADIUS = RADIUS * AVATAR_RADIUS_RATIO

export type BroadcastStatus = "green" | "yellow" | "orange"

export const BROADCAST_STATUS_COLORS: Record<BroadcastStatus, string> = {
    green: "#139E3B", // green-600
    yellow: "#E1AD00", // yellow-500
    orange: "#BB4D00", // yellow-700
}

const BROADCAST_STATUS_THRESHOLDS_MS = {
    green: 30 * 60 * 1000,
    yellow: 10 * 60 * 1000,
}

export const getBroadcastStatus = (
    expiresAt: number,
    now: number = Date.now()
): BroadcastStatus | "ended" => {
    const msRemaining = expiresAt - now
    if (msRemaining <= 0) return "ended"
    if (msRemaining > BROADCAST_STATUS_THRESHOLDS_MS.green) return "green"
    if (msRemaining > BROADCAST_STATUS_THRESHOLDS_MS.yellow) return "yellow"
    return "orange"
}

const hexToRgba = (hex: string, alpha: number) => {
    const value = hex.replace("#", "")
    const r = parseInt(value.substring(0, 2), 16)
    const g = parseInt(value.substring(2, 4), 16)
    const b = parseInt(value.substring(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const { set, getItem, useAll } = createExternalStore<string>()
const pendingFetchKeys = new Set<string>()

const getInitials = (fullName?: string) => {
    if (!fullName) return ""
    const words = fullName.trim().split(/\s+/)
    const firstName = words[0]
    const lastName = words.length >= 2 ? words.at(-1) : undefined
    return `${firstName?.[0] ?? ""}${lastName ? lastName[0] : ""}`.toUpperCase()
}

const createBaseCanvas = () => {
    const canvas = document.createElement("canvas")
    canvas.width = USER_LOCATION_ICON_SIZE
    canvas.height = USER_LOCATION_ICON_SIZE
    return { canvas, ctx: canvas.getContext("2d")! }
}

const drawStatusGlow = (ctx: CanvasRenderingContext2D, color: string) => {
    ctx.save()
    ctx.filter = "blur(18px)"
    ctx.beginPath()
    ctx.arc(RADIUS, RADIUS, AVATAR_RADIUS + 14, 0, Math.PI * 2)
    ctx.fillStyle = hexToRgba(color, 0.55)
    ctx.fill()
    ctx.restore()
}

const drawStatusRing = (ctx: CanvasRenderingContext2D, color: string) => {
    ctx.beginPath()
    ctx.arc(RADIUS, RADIUS, AVATAR_RADIUS + 6, 0, Math.PI * 2)
    ctx.strokeStyle = color
    ctx.lineWidth = 8
    ctx.stroke()
}

const drawHeadingIndicator = (ctx: CanvasRenderingContext2D, color: string) => {
    const tipY = 0
    const baseY = HEADING_INDICATOR_SIZE * 0.7
    const halfWidth = RADIUS * 0.25
    ctx.beginPath()
    ctx.moveTo(RADIUS, tipY)
    ctx.lineTo(RADIUS - halfWidth, baseY)
    ctx.lineTo(RADIUS + halfWidth, baseY)
    ctx.closePath()
    ctx.fillStyle = color
    ctx.strokeStyle = "#FFFFFF"
    ctx.lineWidth = RADIUS * 0.02
    ctx.fill()
    ctx.stroke()
}

const drawInitialsIcon = (userId: string, statusColor: string, fullName?: string) => {
    const { canvas, ctx } = createBaseCanvas()

    drawStatusGlow(ctx, statusColor)

    ctx.beginPath()
    ctx.arc(RADIUS, RADIUS, AVATAR_RADIUS, 0, Math.PI * 2)
    ctx.fillStyle = getColorFromId(userId)
    ctx.fill()

    const initials = getInitials(fullName)
    if (initials) {
        ctx.fillStyle = "#FFFFFF"
        ctx.font = `600 ${Math.round(AVATAR_RADIUS * 0.8)}px sans-serif`
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(initials, RADIUS, RADIUS)
    }

    drawStatusRing(ctx, statusColor)
    drawHeadingIndicator(ctx, statusColor)
    return canvas.toDataURL("image/png")
}

const drawPhotoIcon = (image: HTMLImageElement, statusColor: string) => {
    const { canvas, ctx } = createBaseCanvas()

    drawStatusGlow(ctx, statusColor)

    ctx.save()
    ctx.beginPath()
    ctx.arc(RADIUS, RADIUS, AVATAR_RADIUS, 0, Math.PI * 2)
    ctx.closePath()
    ctx.clip()
    const offset = RADIUS - AVATAR_RADIUS
    ctx.drawImage(image, offset, offset, AVATAR_RADIUS * 2, AVATAR_RADIUS * 2)
    ctx.restore()

    drawStatusRing(ctx, statusColor)
    drawHeadingIndicator(ctx, statusColor)
    return canvas.toDataURL("image/png")
}

const fetchProfilePhotoBlobUrl = async (profilePhotoS3Key: string) => {
    const cachedBlobUrl = getCachedBlobUrl("ProfilePicture", profilePhotoS3Key)
    if (cachedBlobUrl) return cachedBlobUrl
    const res = await fileManagementApi.fetchFile(profilePhotoS3Key, {
        region: SupportedRegions.Canada,
    })
    const blob = await res.blob()
    return addBlobToCache({ type: "ProfilePicture", imageKey: profilePhotoS3Key, imageBlob: blob })
}

const fetchPhotoIcon = async (profilePhotoS3Key: string, statusColor: string, cacheKey: string) => {
    if (pendingFetchKeys.has(cacheKey)) return
    pendingFetchKeys.add(cacheKey)
    try {
        const photoUrl = await fetchProfilePhotoBlobUrl(profilePhotoS3Key)
        const image = new Image()
        await new Promise<void>((resolve, reject) => {
            image.onload = () => resolve()
            image.onerror = () => reject(new Error("Failed to load profile photo"))
            image.src = photoUrl
        })
        set(cacheKey, drawPhotoIcon(image, statusColor))
    } catch (error) {
        console.error("fetchPhotoIcon", error)
    } finally {
        pendingFetchKeys.delete(cacheKey)
    }
}

export const userLocationIconStore = {
    get({
        userId,
        fullName,
        profilePhotoS3Key,
        status = "green",
    }: {
        userId: string
        fullName?: string
        profilePhotoS3Key?: string
        status?: BroadcastStatus
    }) {
        const statusColor = BROADCAST_STATUS_COLORS[status]
        const cacheKey = profilePhotoS3Key
            ? `photo:${profilePhotoS3Key}:${status}`
            : `initials:${userId}:${fullName ?? ""}:${status}`

        const cached = getItem(cacheKey)
        if (cached) return cached

        const initialsIcon = drawInitialsIcon(userId, statusColor, fullName)
        set(cacheKey, initialsIcon)

        if (profilePhotoS3Key) {
            fetchPhotoIcon(profilePhotoS3Key, statusColor, cacheKey).catch((error) =>
                console.error("fetchPhotoIcon", error)
            )
        }

        return initialsIcon
    },
}

export const useUserLocationIconsVersion = useAll
